import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '../../hooks/useAuthContext'

interface SidebarProps {
  activePage: string
  onPageChange: (page: string) => void
  isMobile: boolean
  isOpen: boolean
  onClose: () => void
}

const Sidebar: React.FC<SidebarProps> = ({
  activePage,
  onPageChange,
  isMobile,
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate()
  const { user, logout } = useAuthContext()

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const handleNavItemClick = (page: string) => {
    // Handle navigation logic here
    onPageChange(page)
    // Close sidebar after navigation on mobile
    if (isMobile && onClose) {
      onClose()
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onClose) {
      onClose()
    }
  }

  // Don't render on mobile if not open
  if (isMobile && !isOpen) {
    return null
  }

  const navigationItems = [
    {
      id: 'setup',
      label: 'Setup',
      icon: 'dashboard-nav-icon-setup',
    },
    // Future navigation items can be added here
  ]

  const sidebarContent = (
    <div
      className={`dashboard-sidebar ${isMobile ? 'dashboard-sidebar-mobile' : ''} ${isMobile && isOpen ? 'dashboard-sidebar-mobile-open' : ''}`}
    >
      {/* Mobile Close Button */}
      {isMobile && onClose && (
        <button
          className="dashboard-sidebar-close"
          onClick={onClose}
          aria-label="Close navigation menu"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {/* Logo and Brand */}
      <div className="dashboard-sidebar-header">
        <div className="dashboard-sidebar-logo">
          <img src="/gist-g-logo.png" alt="Ask Anything" className="dashboard-logo" />
          <div className="dashboard-brand-name">
            <span>Ask</span>
            <span>Anything</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="dashboard-sidebar-nav">
        <ul className="dashboard-sidebar-nav-list">
          {navigationItems.map((item) => (
            <li key={item.id} className="dashboard-sidebar-nav-item">
              <button
                className={`dashboard-sidebar-nav-link ${
                  activePage === item.id ? 'dashboard-sidebar-nav-link-active' : ''
                }`}
                onClick={() => handleNavItemClick(item.id)}
              >
                <div className={`dashboard-nav-icon ${item.icon}`}></div>
                <span className="dashboard-sidebar-nav-text">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* User Profile and Actions */}
      <div className="dashboard-sidebar-footer">
        <div className="dashboard-sidebar-user">
          <div className="dashboard-sidebar-user-info">
            <div className="dashboard-sidebar-user-avatar">
              <span className="dashboard-sidebar-user-initial">
                {(user?.name || user?.email || 'U')[0].toUpperCase()}
              </span>
            </div>
            <div className="dashboard-sidebar-user-details">
              <span className="dashboard-sidebar-user-name">{user?.name || 'User'}</span>
              <span className="dashboard-sidebar-user-email">{user?.email}</span>
            </div>
          </div>
          <button className="dashboard-sidebar-logout" onClick={handleLogout} title="Logout">
            <div className="dashboard-nav-icon dashboard-nav-icon-logout"></div>
          </button>
        </div>
      </div>
    </div>
  )

  // On mobile, wrap with backdrop overlay
  if (isMobile) {
    return (
      <div className="dashboard-sidebar-backdrop" onClick={handleBackdropClick}>
        {sidebarContent}
      </div>
    )
  }

  return sidebarContent
}

export default Sidebar
